import React from 'react';
import {debounce} from 'ts-debounce';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {StorableItemType} from 'constants/ModelTypeConstants';
import {StorableSummary} from 'constants/SampleConstants';
import makeCancelable from 'lib/makeCancelable';
import notifications, {Response} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LOCATION_PICKER_SEARCH_FINISHED_EVENT,
  LOCATION_PICKER_SEARCH_STARTED_EVENT,
} from 'performance/processInventoryEvents';
import {inputSearchStorables, StorablesInputSearchResponse} from 'samples/StorableDAO';
import {Filter, FilterKey, Operator, SortKey} from 'search/SearchConstants';

export const MAX_RESULTS = 3;
const DEBOUNCE_MS = 500;

export interface LocationSearchDropdownSearcherProps {
  registryId: string;
  query: string;
  searchableTypes: Array<StorableItemType>;
  includeArchived: boolean;
  onSearchStart: () => void;
  onSearchEnd: (searchResults: {
    query: string;
    results: Array<StorableSummary>;
    errorMessage: string | null;
  }) => void;
  storableSchemaId?: string;
}

interface CancelableInputSearchPromise {
  promise: Promise<StorablesInputSearchResponse>;
  cancel: () => void;
}

/**
 * Takes a query for a storable name and searches for matching storables, calling the appropriate callbacks
 * at the start and end of a search.
 *
 * This component does not render anything and is purely meant to perform a search for storables.
 */
export default class LocationSearchDropdownSearcher extends React.Component<LocationSearchDropdownSearcherProps> {
  _isMounted: boolean = false; // Non-private because tests use this :-\
  private inputSearchPromise?: CancelableInputSearchPromise;

  componentDidMount(): void {
    this._isMounted = true;
    if (this.props.query !== '') {
      this.props.onSearchStart();
      this.doSearchDebounced(this.props.query);
    }
  }

  componentDidUpdate(prevProps: LocationSearchDropdownSearcherProps): void {
    if (this.props.query !== prevProps.query) {
      if (this.props.query !== '') {
        this.props.onSearchStart();
        this.doSearchDebounced(this.props.query);
      } else if (this._isMounted) {
        this.props.onSearchEnd({query: '', results: [], errorMessage: ''});
      }
    }
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  // Non-private because tests use this :-\
  doSearch = (query: string): void => {
    const registryFilter: Array<Filter> =
      this.props.registryId != null
        ? [{key: FilterKey.REGISTRY, operator: Operator.EQ, value: this.props.registryId}]
        : [];
    const schemaFilter: Array<Filter> =
      this.props.storableSchemaId != null
        ? [{key: FilterKey.SCHEMA, operator: Operator.IS_ONE_OF, value: [this.props.storableSchemaId]}]
        : [];

    const archiveFilter: Array<Filter> = this.props.includeArchived
      ? []
      : [
          {
            key: FilterKey.ARCHIVE_PURPOSES,
            operator: Operator.IS_ONE_OF,
            value: [NOT_ARCHIVED_PURPOSE_STRING],
          },
        ];
    const filters: Array<Filter> = [
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: this.props.searchableTypes,
      },
      ...registryFilter,
      ...schemaFilter,
      ...archiveFilter,
    ];

    if (this.inputSearchPromise) {
      this.inputSearchPromise.cancel();
    }

    this.inputSearchPromise = makeCancelable(
      inputSearchStorables({
        query,
        filters,
        limit: MAX_RESULTS,
        offset: 0,
        sorts: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
      })
    );

    logProfilerTimingEvent(LOCATION_PICKER_SEARCH_STARTED_EVENT, {
      component: 'LocationSearchDropdownSearcher',
    });
    this.inputSearchPromise.promise.then(
      (resp: StorablesInputSearchResponse) => {
        if (this._isMounted && query === this.props.query) {
          logProfilerTimingEvent(LOCATION_PICKER_SEARCH_FINISHED_EVENT, {
            searchLimit: MAX_RESULTS,
            component: 'LocationSearchDropdownSearcher',
            numItemsReturned: resp.total,
          });
          this.props.onSearchEnd({query, results: resp.storables, errorMessage: null});
        }
      },
      (error: Response) => {
        if (!error.isCanceled && this._isMounted && query === this.props.query) {
          const errorMessage = notifications.getFriendlyError(error) || 'An error occurred.';
          this.props.onSearchEnd({query, results: [], errorMessage});
        }
      }
    );
  };

  private doSearchDebounced = debounce((query: string) => {
    this.doSearch(query);
  }, DEBOUNCE_MS);

  render(): null {
    return null;
  }
}
